// llm-call-summary — step config summary for canvas flow node.
// Optional runRefs shows KB/KG count chip in footer when run data is available.
// runRefs is absent until StepNode receives run context; chip silently skipped.

import type { ReactNode } from "react";
import { Sparkles, Hash } from "lucide-react";
import { StepInfoRow } from "../../StepInfoRow";
import { getString, getNumber } from "../helpers";
import type { SummaryTranslator } from "../types";

interface LlmRunRefs {
  kbCount?: number;
  kgCount?: number;
}

export function getLlmCallSummary(
  config: Record<string, unknown>,
  t: SummaryTranslator,
  runRefs?: LlmRunRefs
): ReactNode[] {
  const items: ReactNode[] = [];

  const modelId = getString(config.model_id);
  const modelDisplay = getString(config.model_display);
  const prompt = getString(config.prompt_template);
  const responseFormat = getString(config.response_format);
  const maxTokens = getNumber(config.max_tokens);
  const knowledgeIds = Array.isArray(config.knowledge_ids) ? config.knowledge_ids.length : 0;

  if (modelId) {
    items.push(
      <StepInfoRow
        key="model"
        icon={<Sparkles className="size-3.5" />}
        label={t("summaries.llm_call.label_model")}
        value={modelDisplay || t("summaries.shared.unknown_reference")}
      />
    );
  }

  if (prompt) {
    const preview = prompt.length > 80 ? prompt.slice(0, 80) + "…" : prompt;
    items.push(
      <StepInfoRow
        key="prompt"
        icon={<Hash className="size-3.5" />}
        label={t("summaries.llm_call.label_prompt")}
        value={preview}
      />
    );
  }

  if (responseFormat) {
    items.push(
      <StepInfoRow
        key="format"
        icon={<Hash className="size-3.5" />}
        label={t("summaries.llm_call.label_format")}
        value={responseFormat}
        mono
      />
    );
  }

  if (maxTokens) {
    items.push(
      <StepInfoRow
        key="tokens"
        icon={<Hash className="size-3.5" />}
        label={t("summaries.llm_call.label_max_tokens")}
        value={String(maxTokens)}
        mono
      />
    );
  }

  if (knowledgeIds > 0) {
    items.push(
      <StepInfoRow
        key="kb"
        icon={<Hash className="size-3.5" />}
        label={t("summaries.llm_call.label_knowledge")}
        value={t("summaries.llm_call.knowledge_count", { count: knowledgeIds })}
      />
    );
  }

  // Run-time KB/KG ref count chip — only when run data available (StepNode wired later).
  if (runRefs && (runRefs.kbCount || runRefs.kgCount)) {
    const parts: string[] = [];
    if (runRefs.kbCount) parts.push(`${runRefs.kbCount} KB`);
    if (runRefs.kgCount) parts.push(`${runRefs.kgCount} KG`);
    items.push(
      <StepInfoRow
        key="run-refs"
        icon={<Hash className="size-3.5" />}
        label={t("summaries.llm_call.label_refs")}
        value={parts.join(" · ")}
      />
    );
  }

  return items;
}
